import { Breadcrumbs, Input, Multiselect, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import { apiService } from 'services/base.service';
import { useLocation, useParams } from 'react-router-dom';
import { multiselectOptionsType } from '../convert-meal-category-to-addon-category';
import useDashboardContext from 'contexts/dashboard.context';
import { BaseAddonCategoryType, BrandType, MealType } from 'utils/types';
import { toast } from 'react-toastify';
import SingleBottomNav from 'components/base/single-bottom-nav';
import { ROUTE_KEYS } from 'utils/constants';
import Select from 'components/base/select';
import { getBaseAddonCategory } from 'pages/base-items/util';

type PayloadType = {
  meal_id: string;
  parent_meal_id: any;
  is_new: boolean;
  meal_addon_category_id: any;
  addon_category_name: string;
  addon_category_description: string;
  max_selection_no: string;
  min_selection_no: string;
  meal_category_selction_type: string[];
  base_addon_category_id: string;
};
const ConvertMealtoAddon = () => {
  const location = useLocation();
  const { id } = useParams();
  const { category, activeCokitchen } = useDashboardContext();
  const [parentMealList, setParentMealList] = useState([] as any);
  const [baseAddonCategory, setBaseAddonCategory] = useState([] as BaseAddonCategoryType[]);
  const [selectedBrand, setSelectedBrand] = useState([] as string[]);
  const [brandsList, setBrandsList] = useState([] as multiselectOptionsType[]);
  const [formData, setFormData] = useState({
    meal_id: id,
    meal_addon_category_id: [],
    parent_meal_id: [],
    is_new: false
  } as PayloadType);

  const [addonCategoryList, setAddonCategoryList] = useState([] as any);
  const [loading, setLoading] = useState(false);
  const [mealData, setMealData] = useState<MealType | null>(null);
  const fetchBrandData = async () => {
    try {
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const brands = res.data
        ?.filter((b: BrandType) => b.item_type === category.toUpperCase() && !b.archived)
        .map((brand: BrandType) => ({
          text: brand?.name,
          value: brand?.id
        }));
      setBrandsList(brands);
    } catch {
      setBrandsList([]);
    }
  };
  useEffect(() => {
    fetchBrandData();
  }, [activeCokitchen]);

  const fetchParentMeals = async () => {
    try {
      const res = await apiService.post(`meals`, {
        data: { brand_ids: selectedBrand },
        isInternal: true
      });
      const { data } = res.data;
      const parentMealsList = data.map((parentMeal: any) => {
        return {
          value: parentMeal?.id,
          text: `${parentMeal?.name} - ${parentMeal?.brand?.name} `
        };
      });
      setParentMealList(parentMealsList);
    } catch {
      setParentMealList([]);
    }
  };

  const fetchAddonCategories = async () => {
    try {
      const res = await apiService.post(`internal/meal-addon-category`, {
        data: { meal_ids: formData?.parent_meal_id },
        isInternal: true
      });
      const { data } = res.data;
      const addonCategoryList = data.map((selectionType: any) => {
        return {
          value: selectionType?.id,
          text: `${selectionType?.meal.brand.name} - ${selectionType?.base_addon_category.name}`
        };
      });
      setAddonCategoryList(addonCategoryList);
    } catch {
      setAddonCategoryList([]);
    }
  };
  useEffect(() => {
    fetchAddonCategories();
  }, [formData?.parent_meal_id]);
  useEffect(() => {
    fetchParentMeals();
  }, [selectedBrand]);

  const handleConfirm = async () => {
    try {
      const payload = formData;
      payload.parent_meal_id = formData.parent_meal_id[0];
      payload.meal_addon_category_id = formData.meal_addon_category_id[0];
      if (formData?.is_new) delete payload.meal_addon_category_id;
      const res = await apiService.post('marketing/meals/convert-meal-to-addon', {
        data: {
          ...formData
        }
      });
      if (res?.data) {
        toast(<ToastMessage type='success' message='Meal successfully converted to addon' />);
      }
    } catch {
      toast(
        <ToastMessage type='error' message='Error encountered while converting meal to addon' />
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchMealData = async () => {
    try {
      const response = await apiService.get(`meal/${id}`);
      const { data } = response.data;
      setMealData(data);
    } catch (error: any) {
      console.log({ error });
    }
  };

  useEffect(() => {
    fetchMealData();
    fetchBaseAddonCategory();
  }, []);

  const fetchBaseAddonCategory = async () => {
    const categories = await getBaseAddonCategory();
    setBaseAddonCategory(categories);
  };

  const breadcrumbs = [
    {
      name: mealData?.brand?.name ? `View ${mealData.brand.name}` : '...',
      path: `${ROUTE_KEYS.MEALS}?brand_id=${mealData?.brand?.id}`
    },
    {
      name: mealData?.meal_category?.name ?? '...',
      path: `${ROUTE_KEYS.MEALS}/${mealData?.meal_category_id}`
    },
    {
      name: 'Convert meal',
      path: ''
    }
  ];

  const validator =
    formData.parent_meal_id.length > 0 &&
    (formData.meal_addon_category_id.length > 0 || formData.base_addon_category_id);

  return (
    <div className='max-w-[755px]'>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <PageHeaderText
        title={`Convert ${location.state}`}
        description='This lets you convert a meal to an add-on'
      />
      <div className='mb-[24px]'>
        <p className='font-medium text-sm'>Select Brand</p>
        <Multiselect
          placeholder='Select brand '
          values={selectedBrand}
          options={brandsList}
          onChange={setSelectedBrand}
        />
      </div>
      <div className='mb-[24px]'>
        <p className='font-medium text-sm'>Select Parent meal</p>
        <Multiselect
          placeholder='Select meal'
          values={formData.parent_meal_id}
          options={parentMealList}
          onChange={(e) => {
            setFormData({ ...formData, parent_meal_id: e });
          }}
        />
      </div>
      <div className='mb-[24px]'>
        <p className='font-medium text-sm'>Select Addon Category</p>
        <Multiselect
          placeholder='Select addon Category'
          values={formData?.meal_addon_category_id}
          options={addonCategoryList}
          onChange={(k) => setFormData({ ...formData, meal_addon_category_id: k })}
          isSingle
        />
      </div>
      <div className='flex justify-start gap-4 items-center bg-[#EEECEC] pl-[20px] py-3 my-4 rounded border-[0.5px] text-xs border-[#EEECEC]'>
        <Input
          type='checkbox'
          name='is_new'
          checked={formData?.is_new}
          onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
          label='Add new addon category'
        />
      </div>
      {formData?.is_new && (
        <div>
          <p className='text-xs'>Base addon category</p>
          <Select
            small
            hasSearch
            //loading={loading}
            value={formData.base_addon_category_id}
            options={baseAddonCategory.map((item) => {
              return { value: item.id, text: item.name };
            })}
            placeholder='Select base addon category'
            onChange={(v) => setFormData({ ...formData, base_addon_category_id: v })}
          />
        </div>
      )}
      <SingleBottomNav loading={loading} onConfirm={handleConfirm} btnDisabled={!validator} />
    </div>
  );
};

export default ConvertMealtoAddon;
